import { insuranceTypes } from '@/src/constants/itinerary/insurance'
import {
    AMEND_INSURANCE,
    DOM_CANCELATION_INSURANCE,
    INTL_CANCELATION_INSURANCE,
    INTL_FLEXIFLY
} from '@/src/constants/itnerary'
import {
    Benefit,
    Fare,
    Flight,
    FlightSegment,
    ItineraryResponse,
    PaxPricingInfo,
    VasAncillary
} from '@/src/types/itinerary'
import { getFlightLogoUrl } from '@/src/utils/generalUtils/airlineImage'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import { isNextDay } from '@/src/utils/itinerary/dateTransformer'
import { logError } from '@/src/utils/logError'
import { getTotalFlightDuration } from '../flightUtils'

/** Baggage format accepted by SegmentBaggage (cab=cabin/hand, cib=check-in) */
export interface TransformedSegmentBaggage {
    cab?: string
    cib?: string
    freeText?: string
}

export interface TransformedSegmentData {
    segmentKey: string
    route: string
    departureTime: string
    arrivalTime: string
    departureCode: string
    arrivalCode: string
    departureAirport: string
    departureTerminal: string
    arrivalAirport: string
    arrivalTerminal: string
    airlineName: string
    airlineLogo: string
    flightNumber: string
    cabinType: string
    duration: { hours: number; mins: number }
    airline: string
    arrivalDate?: string
    departureDate?: string
    srpFlightDetails?: boolean
    operatingAirlineName: string
    /** Raw aircraft code for aircraft-amenities BFF (maps to SRP `act`). */
    airCraftType?: string | null
    baggage?: TransformedSegmentBaggage
}

export interface TransformedLayoverData {
    layoverType: string
    layoverDuration: { hours: number; mins: number }
    airportCode: string
    layoverAirport: string
    cityName: string
}

export interface ProcessedFlightData {
    id: string
    airlineLogo: string
    airlineName: string
    flightNumber: string
    fromCity: string
    toCity: string
    isNextDay: boolean
    departureTime: string
    arrivalTime: string
    duration: { hours: number; mins: number }
    stopCount: number
    stopsText: string
    cabinType: string
}

export const getHoursAndMinutes = (seconds: number) => {
    const totalMinutes = Math.floor(seconds / 60)
    const hours = Math.floor(totalMinutes / 60)
    const mins = totalMinutes % 60
    return { hours, mins }
}

const formatAirportName = (airportName: string, terminal: string): string => {
    if (terminal) {
        return `${airportName}, Terminal ${terminal}`
    }
    return airportName
}

// Helper function to format stops text
const formatStopsText = (stopCount: number): string => {
    if (stopCount === 0) return 'Non-stop'
    if (stopCount === 1) return '1 stop'
    return `${stopCount} stops`
}

export const transformSegmentData = (segment: FlightSegment): TransformedSegmentData => {
    // Create route string
    const route = `${segment.departAirport} → ${segment.arrivalAirport}`
    const segmentKey = `${segment.departAirport}_${segment.arrivalAirport}_${segment.departDateTime}`
    const operatingAirlineName = segment.airline !== segment.operatingAirline ? segment.operatingAirlineName : ''

    return {
        route,
        segmentKey,
        departureTime: segment.formattedDepartDateTime,
        arrivalTime: segment.formattedArrivalDateTime,
        departureCode: segment.departAirport,
        arrivalCode: segment.arrivalAirport,
        departureAirport: segment.departAirportIata,
        departureTerminal: segment.departTerminal,
        arrivalAirport: segment.arrivalAirportIata,
        arrivalTerminal: segment.arrivalTerminal,
        airlineName: segment.airlineName,
        airlineLogo: getFlightLogoUrl(segment.airline),
        flightNumber: `${segment.airline}-${segment.flightNumber}`,
        cabinType: segment.cabinType,
        duration: getHoursAndMinutes(segment.duration),
        airline: segment.airline,
        operatingAirlineName,
        airCraftType: segment.airCraftType || null
    }
}

export const transformLayoverData = (
    currentSegment: FlightSegment,
    nextSegment: FlightSegment,
    layoverDurationSeconds: number
): TransformedLayoverData => {
    // Convert seconds to minutes for layover type calculation
    const layoverDurationMinutes = Math.floor(layoverDurationSeconds / 60)

    const layoverType =
        currentSegment.flightNumber === nextSegment.flightNumber
            ? 'Technical layover'
            : layoverDurationMinutes <= 240
              ? 'Short layover'
              : 'Long layover'

    const layoverDuration = getHoursAndMinutes(layoverDurationSeconds)
    const layoverAirport = currentSegment.arrivalAirportIata
    const airportCode = currentSegment.arrivalAirport
    const cityName = currentSegment.arrivalAirportIata.split(',')[1]

    return {
        layoverType,
        layoverDuration,
        layoverAirport,
        airportCode,
        cityName
    }
}

export const processFlightsForReview = (flights: Flight[]): ProcessedFlightData[] => {
    return flights.map((flight, index) => {
        const firstSegment = flight.segments[0]
        const lastSegment = flight.segments[flight.segments.length - 1]
        const transformedFirst = transformSegmentData(firstSegment)
        const transformedLast = transformSegmentData(lastSegment)
        const stopCount = flight.segments.length - 1
        const duration = getHoursAndMinutes(getTotalFlightDuration(flight))

        return {
            id: `flight-${index}`,
            airlineLogo: transformedFirst.airlineLogo,
            airlineName: transformedFirst.airlineName,
            flightNumber: transformedLast.flightNumber,
            fromCity: flight.fromCity,
            toCity: flight.toCity,
            isNextDay: isNextDay(firstSegment.departDateTime, lastSegment.arrivalDateTime),
            departureTime: transformedFirst.departureTime,
            arrivalTime: transformedLast.arrivalTime,
            duration,
            stopCount,
            stopsText: formatStopsText(stopCount),
            cabinType: transformedFirst.cabinType.toUpperCase()
        }
    })
}

const getFlexiflyBenefits = (vasBenefits: Benefit[] = []) => {
    const benefitsArr: string[] = []
    if (Array.isArray(vasBenefits)) {
        vasBenefits.forEach(benefit => {
            benefitsArr.push(benefit.title)
        })
    }
    return benefitsArr
}

const getVasBundledFare = (fightFare: Fare, vas: VasAncillary): Fare => {
    const { basePrice, price, taxes, discount, markup, convenienceFee, totalVat, vatPercentage } = fightFare
    const { vasDetails } = vas || {}
    const { provider, benefits, totalPremiumAmount, selected, benefitGroups } = vasDetails?.[0] || {}

    const fare: Partial<Fare> = {
        basePrice,
        price,
        taxes,
        totalVat,
        vatPercentage,
        discount,
        markup,
        convenienceFee,
        totalPremiumAmount,
        vatAmountOnPremium: vasDetails?.[0]?.vatAmount || 0,
        fareClass: 'RETAIL',
        fareType: null,
        fareMetaMessages: [
            {
                provider,
                category: vas.vasType,
                title: vas.vasType === INTL_CANCELATION_INSURANCE ? 'EzCancel' : 'Flexifly',
                messages: getFlexiflyBenefits(benefits || benefitGroups?.[0]?.benefits)
            }
        ],
        fareKey: null,
        selected
    }
    return fare as Fare
}

export const createVasBundledFares = (segmentFares: Fare[], vasAncillaries: VasAncillary[]) => {
    let fares: Fare[] = [segmentFares[0]]

    vasAncillaries.forEach(vas => {
        const isVasTypeEligible =
            [AMEND_INSURANCE, INTL_FLEXIFLY, DOM_CANCELATION_INSURANCE, INTL_CANCELATION_INSURANCE].includes(
                vas.vasType
            ) && vas.eligible

        if (isVasTypeEligible) {
            const vasBundledFare = getVasBundledFare(segmentFares[0], vas)
            fares.push(vasBundledFare)
        }
    })
    if (
        fares.length === 3 &&
        (fares[1].fareMetaMessages[0].category === DOM_CANCELATION_INSURANCE ||
            fares[1].fareMetaMessages[0].category === INTL_CANCELATION_INSURANCE)
    ) {
        ;[fares[1], fares[2]] = [fares[2], fares[1]]
    }
    return fares
}

export const getJourneyPricingFromPaxPricingInfo = (paxPricingInfoJson: PaxPricingInfo[]) => {
    const { basePrice, taxes, vat, instantDiscount } = paxPricingInfoJson.reduce(
        (acc, curr) => {
            const { pricingInfoJson } = curr || {}
            const pricingElements = (pricingInfoJson || []).flatMap(info => info?.pricingElementJson || [])
            const basePrice = pricingElements
                .filter(e => ['BF', 'MKP'].includes(e.category))
                .reduce((sum: number, e) => sum + (e.amount || 0), 0)
            const taxes = pricingElements
                .filter(e => e.category === 'TAX' || ['AIRLINE-VAT', 'CT-VAT'].includes(e.code))
                .reduce((sum: number, e) => sum + (e.amount || 0), 0)
            const vat = pricingElements
                .filter(e => ['AIRLINE-VAT', 'CT-VAT'].includes(e.code))
                .reduce((sum: number, e) => sum + (e.amount || 0), 0)
            const instantDiscount = pricingElements
                .filter(e => e.category === 'DIS')
                .reduce((sum, e) => sum + (Math.abs(e.amount) || 0), 0)
            return {
                basePrice: acc.basePrice + basePrice,
                taxes: acc.taxes + taxes,
                vat: acc.vat + vat,
                instantDiscount: acc.instantDiscount + instantDiscount
            }
        },
        { basePrice: 0, taxes: 0, vat: 0, instantDiscount: 0 }
    )
    return { basePrice, taxes, totalVat: vat, price: basePrice + taxes - instantDiscount }
}

export const getFares = (flights: Flight[], paxPricingInfoJson: PaxPricingInfo[], vasAncillaries: VasAncillary[]) => {
    try {
        const { basePrice, taxes, totalVat, price } = getJourneyPricingFromPaxPricingInfo(paxPricingInfoJson)
        let baseFare: Fare = { ...flights[0].fares[0], basePrice, price, taxes, totalVat }
        let tripFareList: Fare[][] = []
        // flights.forEach((segment, index) => {
        //     if (index != 0) {
        //         const {
        //             basePrice: journeyLegBasePrice,
        //             price: journeyLegPrice,
        //             taxes: journeyLegTaxes
        //         } = segment.fares[0]
        //         baseFare = {
        //             ...baseFare,
        //             basePrice: baseFare.basePrice + journeyLegBasePrice,
        //             price: baseFare.price + journeyLegPrice,
        //             taxes: baseFare.taxes + journeyLegTaxes
        //         }
        //     }
        // })
        if (!isEmpty(vasAncillaries)) {
            tripFareList.push(createVasBundledFares([baseFare], vasAncillaries))
        } else {
            tripFareList.push([baseFare])
        }

        const fareRow = tripFareList[0]
        const [baseFareCard, ...addonFareCards] = fareRow
        const hasSelectedAddon = addonFareCards.some((fare: Fare) => fare.selected)
        if (hasSelectedAddon && baseFareCard) {
            baseFareCard.selected = false
        }

        // default select base fare when nothing is selected
        const selectedFare = fareRow.find((fare: Fare) => fare.selected)
        if (!selectedFare) {
            fareRow[0].selected = true
        }
        return tripFareList
    } catch (error) {
        logError('getFares', error)
        return [[]]
    }
}

export const getInsurances = (vasAncillaries: VasAncillary[]) => {
    let availableInsurances: VasAncillary[] = []
    try {
        if (Array.isArray(vasAncillaries)) {
            vasAncillaries.forEach(vas => {
                const { vasType } = vas || {}
                if (insuranceTypes.includes(vasType)) {
                    availableInsurances.push(vas)
                }
            })
        }

        availableInsurances.sort((insuranceA, insuranceB) => {
            const autoSelectedA = insuranceA?.vasDetails?.at(0)?.autoSelected
            const autoSelectedB = insuranceB?.vasDetails?.at(0)?.autoSelected
            return autoSelectedA === true ? -1 : autoSelectedB === true ? 1 : 0
        })
    } catch (e) {
        logError('getInsurances', e)
    }

    return availableInsurances
}

export const getSelectedInsurance = (insuranceList: VasAncillary[]) => {
    let selectedInsurances: VasAncillary[] = []
    try {
        if (Array.isArray(insuranceList)) {
            selectedInsurances = insuranceList.filter(insurance => {
                const { eligible, vasDetails } = insurance || {}
                if (!eligible) {
                    return false
                }
                const { selected } = vasDetails?.[0] || {}
                return selected
            })
        }
    } catch (e) {
        logError('getSelectedInsurance', e)
        selectedInsurances = []
    }
    return selectedInsurances
}
export const airportChangeHandler = (itiData: Partial<ItineraryResponse> = {}) => {
    const from = (itiData.searchCriteria && (itiData.searchCriteria as any).from) || ''
    const to = (itiData.searchCriteria && (itiData.searchCriteria as any).to) || ''
    const flights = itiData.flights ?? []

    const getCodes = (flight: any) => {
        const segments = flight?.segments ?? []
        return {
            departure: segments[0]?.departAirport ?? '',
            arrival: segments[segments.length - 1]?.arrivalAirport ?? ''
        }
    }

    const onward = flights[0] ? getCodes(flights[0]) : { departure: '', arrival: '' }
    const ret = flights[1] ? getCodes(flights[1]) : { departure: '', arrival: '' }

    const isAirportChangeFlag =
        flights.length > 1
            ? from !== onward.departure || to !== onward.arrival || from !== ret.arrival || to !== ret.departure
            : from !== onward.departure || to !== onward.arrival

    return isAirportChangeFlag
}

export const checkIfTerminalChange = (flights: Flight[]) => {
    try {
        let isTerminalChange = false
        flights.forEach(flight => {
            const { segments = [] } = flight || {}
            const nextAiportObj = { departureAirportName: '' }
            segments?.forEach(segment => {
                const {
                    departAirportIata = '',
                    arrivalAirportIata = '',
                    departTerminal,
                    arrivalTerminal
                } = segment || {}
                const arrivalAirportName = arrivalAirportIata?.split(',')[0]
                const departureAirportName = departAirportIata?.split(',')[0]
                if (!isTerminalChange) {
                    if (
                        !isEmpty(nextAiportObj.departureAirportName) &&
                        nextAiportObj.departureAirportName !== `${departureAirportName}${departTerminal}`
                    ) {
                        isTerminalChange = true
                    } else {
                        nextAiportObj.departureAirportName = `${arrivalAirportName}${arrivalTerminal}`
                    }
                }
            })
        })
        return isTerminalChange
    } catch (error) {
        logError('checkIfTerminalChange', error)
        return false
    }
}
